import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingApi } from '../../../utils/bookingApi';
import { PageHeader, Picker, Skeleton, ErrorState, useToast, useConfirm, useUnsavedChanges, DISCARD } from '../../../components/ui';
import { CONCERN_TYPE_LABEL, SEVERITY_LABEL } from '../../../components/welfare/welfareLabels';
import { WELFARE_PATH } from './AdminWelfare';
import '../booking-shared.css';
import './Safeguarding.css';

const EMPTY_FORM = {
  gymnastId: '',
  subjectUserId: '',
  incidentDate: '',
  location: '',
  concernType: 'SAFEGUARDING',
  severity: 'MINOR',
  description: '',
  actionTaken: '',
  outcome: '',
  witnessName: '',
  witnessContact: '',
  witnessStatement: '',
  referredExternally: false,
  referralDetails: '',
};

const Optional = () => <span className="bk-optional"> (optional)</span>;

// Gymnasts and adults share one list, so each gets a key that says which
export const personKey = (type, id) => `${type}-${id}`;
export function personItems(people) {
  return people.map(p => ({
    id: personKey(p.type, p.id),
    label: `${p.firstName} ${p.lastName}`,
    detail: `${p.label}${p.archived ? ' (archived)' : ''}`,
    search: `${p.label}${p.archived ? ' archived' : ''}`,
  }));
}

function WelfareFormFields({ form, setForm, people, errors }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const items = useMemo(() => personItems(people), [people]);
  const chosen = form.gymnastId ? personKey('gymnast', form.gymnastId)
    : form.subjectUserId ? personKey('user', form.subjectUserId) : '';
  const choose = (key) => {
    const at = key.indexOf('-');
    const type = key.slice(0, at);
    const id = key.slice(at + 1);
    setForm(f => ({
      ...f,
      gymnastId: key && type === 'gymnast' ? id : '',
      subjectUserId: key && type === 'user' ? id : '',
    }));
  };
  return (
    <>
      <div className="bk-label">
        <label htmlFor="welfare-person">Person involved<Optional /></label>
        <Picker
          id="welfare-person"
          label="Person involved"
          items={items}
          value={chosen}
          onChange={choose}
          placeholder="Search for a gymnast, coach or parent…"
        />
      </div>
      <div className="bk-grid-2">
        <label className="bk-label">
          Date and time
          <input type="datetime-local" className="bk-input" value={form.incidentDate ? form.incidentDate.slice(0, 16) : ''}
            onChange={e => set('incidentDate', e.target.value)} aria-invalid={errors.incidentDate ? 'true' : undefined} />
          {errors.incidentDate && <span className="bk-field-error">{errors.incidentDate}</span>}
        </label>
        <label className="bk-label">
          Location<Optional />
          <input type="text" className="bk-input" value={form.location || ''} onChange={e => set('location', e.target.value)}
            placeholder="For example, main gym floor" />
        </label>
        <label className="bk-label">
          Concern type
          <select className="bk-input" value={form.concernType} onChange={e => set('concernType', e.target.value)}>
            {Object.entries(CONCERN_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="bk-label">
          Severity
          <select className="bk-input" value={form.severity} onChange={e => set('severity', e.target.value)}>
            {Object.entries(SEVERITY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <label className="bk-label">
        What was seen or reported
        <span className="sg-hint">Describe the concern in as much detail as you can.</span>
        <textarea className="bk-input" rows={4} value={form.description} onChange={e => set('description', e.target.value)}
          aria-invalid={errors.description ? 'true' : undefined} />
        {errors.description && <span className="bk-field-error">{errors.description}</span>}
      </label>
      <label className="bk-label">
        Action taken<Optional />
        <textarea className="bk-input" rows={2} value={form.actionTaken || ''} onChange={e => set('actionTaken', e.target.value)} />
      </label>
      <label className="bk-label">
        Outcome<Optional />
        <span className="sg-hint">What happened in the end, or the next steps.</span>
        <textarea className="bk-input" rows={2} value={form.outcome || ''} onChange={e => set('outcome', e.target.value)} />
      </label>
      <fieldset>
        <legend>Witness account</legend>
        <div className="bk-grid-2">
          <label className="bk-label">
            Witness name<Optional />
            <input type="text" className="bk-input" value={form.witnessName || ''} onChange={e => set('witnessName', e.target.value)} />
          </label>
          <label className="bk-label">
            Phone or email<Optional />
            <input type="text" className="bk-input" value={form.witnessContact || ''} onChange={e => set('witnessContact', e.target.value)} />
          </label>
        </div>
        <label className="bk-label">
          Witness statement<Optional />
          <textarea className="bk-input" rows={3} value={form.witnessStatement || ''} onChange={e => set('witnessStatement', e.target.value)} />
        </label>
      </fieldset>
      <fieldset>
        <legend>External referral</legend>
        <label className="sg-check">
          <input type="checkbox" className="bk-checkbox" checked={form.referredExternally || false}
            onChange={e => set('referredExternally', e.target.checked)} />
          Referred to an outside agency, such as social services or a safeguarding lead
        </label>
        {form.referredExternally && (
          <label className="bk-label">
            Referral details<Optional />
            <textarea className="bk-input" rows={2} value={form.referralDetails || ''} onChange={e => set('referralDetails', e.target.value)}
              placeholder="For example, who was contacted and any reference numbers" />
          </label>
        )}
      </fieldset>
    </>
  );
}

function check(form) {
  const errors = {};
  if (!form.incidentDate) errors.incidentDate = 'Enter the date and time';
  if (!form.description?.trim()) errors.description = 'Describe the concern';
  return errors;
}

// A new welfare report (/new), or changing one (/:id/edit)
export default function AdminWelfareForm() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [people, setPeople] = useState([]);
  const [loaded, setLoaded] = useState(null);
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    Promise.all([
      bookingApi.getWelfarePeople(),
      isNew ? Promise.resolve({ data: EMPTY_FORM }) : bookingApi.getWelfareReport(id),
    ]).then(([pRes, rRes]) => {
      setPeople(pRes.data);
      const start = { ...EMPTY_FORM, ...rRes.data };
      setLoaded(start);
      setForm(start);
    }).catch((err) => { console.error('Could not load the welfare report', err); setLoadError(true); });
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  const changed = !!form && JSON.stringify(form) !== JSON.stringify(loaded);
  useUnsavedChanges(changed && !saving);

  const back = isNew
    ? { to: WELFARE_PATH, label: 'Welfare reports' }
    : { to: `${WELFARE_PATH}/${id}`, label: 'Welfare report' };

  const leave = (to) => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(to, { replace: true });
  };
  const cancel = async () => {
    if (changed && !(await confirm(DISCARD))) return;
    leave(back.to);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = check(form);
    setErrors(found);
    if (Object.keys(found).length) {
      setFormError('Fill in the highlighted fields.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (isNew) {
        await bookingApi.createWelfareReport({
          ...form,
          incidentDate: new Date(form.incidentDate).toISOString(),
          gymnastId: form.gymnastId || undefined,
          subjectUserId: form.subjectUserId || undefined,
        });
        toast('Welfare report saved');
        leave(WELFARE_PATH);
      } else {
        await bookingApi.updateWelfareReport(id, {
          gymnastId: form.gymnastId || '',
          subjectUserId: form.subjectUserId || '',
          incidentDate: form.incidentDate,
          location: form.location,
          concernType: form.concernType,
          severity: form.severity,
          description: form.description,
          actionTaken: form.actionTaken,
          outcome: form.outcome,
          witnessName: form.witnessName,
          witnessContact: form.witnessContact,
          witnessStatement: form.witnessStatement,
          referredExternally: form.referredExternally,
          referralDetails: form.referralDetails,
        });
        toast('Welfare report saved');
        leave(`${WELFARE_PATH}/${id}`);
      }
    } catch (err) {
      setFormError(err.response?.data?.error || "Couldn't save the report. Try again.");
      setSaving(false);
    }
  };

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader back={back} title={isNew ? 'New welfare report' : 'Edit welfare report'} />
      {loadError && <ErrorState title="Couldn't load the report" onRetry={load} />}
      {!loadError && !form && <Skeleton rows={5} label="Loading…" />}
      {form && (
        <form className="sg-form" onSubmit={handleSubmit} noValidate>
          <WelfareFormFields form={form} setForm={setForm} people={people} errors={errors} />
          {formError && <p className="bk-form-error" role="alert">{formError}</p>}
          <div className="bk-actions">
            <button type="submit" className="bk-btn bk-btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            <button type="button" className="bk-btn" disabled={saving} onClick={cancel}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
